/* =============================================================================
   DAILE Mono — behaviour for layout/mono.liquid (MVP 2026-10-05). No dependencies.

   1. Light / dark theme toggle (data-theme on <html>, localStorage 'mn-theme')
   2. Scroll reveal (IntersectionObserver → .is-in; rails reveal as a group)
   3. Announcement bar rotator (no free-shipping amount any more — owner, round 7)
   4. Header: sticky state, side menu (<dialog> drawer, sub-lists), search panel, <details> auto-close,
      auto-submitting selects (PDP), cart badge
   5. Rails (carousels): arrows, dots, disabled ends — scroll-snap does the moving
   6. Customer reels: play on demand, one at a time, pause off screen
   7. Autoplay videos (NFC): muted loop only while on screen; pause button
   8. Theme editor re-init
   9. Preview links: keep ?view=mono… on internal links / forms while Mono is previewed (catalog group)
   10. Promo hero sign-up (mn-promo): window.mnOffer.subscribe() or the native Shopify customer form
   11. Gift-set chooser (mn-sets): "Make it personal" / "Choose an artwork" dialog per set
   12. Country & language modal (snippets/mn-locale): window.mnLocale.open() / .close(), [data-mn-locale-open]
   13. Card photo on touch (catalog cards + homepage rail, round 12): a light touch shows the framed artwork

   Motion only with transform / opacity; no per-frame scroll handlers.
   prefers-reduced-motion: no auto-rotation, no autoplay, content shown at once.
   ============================================================================= */
(() => {
  'use strict';

  const d = document;
  const html = d.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const design = !!(window.Shopify && window.Shopify.designMode) || html.classList.contains('mn-design');
  const hasIO = 'IntersectionObserver' in window;
  const THEME_KEY = 'mn-theme';

  const once = (el, key) => {
    if (el.hasAttribute(`data-mn-ready-${key}`)) return false;
    el.setAttribute(`data-mn-ready-${key}`, '');
    return true;
  };

  /* 1. Theme ================================================================ */

  const storedTheme = () => {
    try {
      const t = window.localStorage.getItem(THEME_KEY);
      return t === 'light' || t === 'dark' ? t : null;
    } catch (e) {
      return null;
    }
  };

  const applyTheme = (theme, persist) => {
    html.setAttribute('data-theme', theme);
    if (persist) {
      try { window.localStorage.setItem(THEME_KEY, theme); } catch (e) { /* private mode */ }
    }
    d.querySelectorAll('[data-mn-theme-toggle]').forEach((b) => b.setAttribute('aria-pressed', String(theme === 'dark')));
    d.querySelectorAll('[data-mn-theme-set]').forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-mn-theme-set') === theme)));
    d.querySelectorAll('meta[name="theme-color"]').forEach((m) => m.setAttribute('content', theme === 'dark' ? '#0b0b0b' : '#ffffff'));
  };

  const switchTheme = (theme) => {
    if (theme === html.getAttribute('data-theme')) {
      applyTheme(theme, true);
      return;
    }
    if (!reduce.matches && typeof d.startViewTransition === 'function') {
      d.startViewTransition(() => applyTheme(theme, true));
    } else {
      applyTheme(theme, true);
    }
  };

  applyTheme(html.getAttribute('data-theme') === 'dark' ? 'dark' : 'light', false);

  const systemDark = window.matchMedia('(prefers-color-scheme: dark)');
  const onSystem = (e) => { if (!storedTheme()) applyTheme(e.matches ? 'dark' : 'light', false); };
  if (systemDark.addEventListener) systemDark.addEventListener('change', onSystem);

  d.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-mn-theme-toggle]');
    if (toggle) {
      switchTheme(html.getAttribute('data-theme') === 'dark' ? 'light' : 'dark');
      return;
    }
    const set = e.target.closest('[data-mn-theme-set]');
    if (set) switchTheme(set.getAttribute('data-mn-theme-set'));
  });

  /* 2. Reveal =============================================================== */

  const initReveal = (root) => {
    const els = Array.from(root.querySelectorAll('.mn-reveal:not(.is-in), .mn-slide:not(.is-in), [data-mn-timeline]:not(.is-in)'));
    if (!els.length) return;
    if (design || reduce.matches || !hasIO) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const groups = new Map();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const target = entry.target;
        io.unobserve(target);
        if (groups.has(target)) groups.get(target).forEach((el) => el.classList.add('is-in'));
        else target.classList.add('is-in');
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });

    els.forEach((el) => {
      const parent = el.parentElement;
      if (parent && parent.hasAttribute('data-mn-rail-track')) {
        if (!groups.has(parent)) {
          groups.set(parent, []);
          io.observe(parent);
        }
        groups.get(parent).push(el);
      } else {
        io.observe(el);
      }
    });
  };

  /* 3. Announcement ========================================================= */

  const initAnn = (root) => {
    root.querySelectorAll('[data-mn-ann]').forEach((bar) => {
      if (!once(bar, 'ann')) return;

      const items = Array.from(bar.querySelectorAll('[data-mn-ann-item]'));
      if (items.length < 2) return;
      const wide = window.matchMedia('(min-width: 990px)');
      const rowMode = () => bar.classList.contains('mn-ann--row') && wide.matches;
      const interval = Math.max(3, parseFloat(bar.getAttribute('data-mn-interval')) || 5) * 1000;
      let index = 0;
      let timer = null;
      let hold = false;

      const show = (n) => {
        index = (n + items.length) % items.length;
        items.forEach((el, k) => el.classList.toggle('is-active', k === index));
      };
      const stop = () => {
        if (timer) window.clearInterval(timer);
        timer = null;
      };
      const start = () => {
        stop();
        if (reduce.matches || design || rowMode() || hold || d.hidden) return;
        timer = window.setInterval(() => show(index + 1), interval);
      };

      const prev = bar.querySelector('[data-mn-ann-prev]');
      const next = bar.querySelector('[data-mn-ann-next]');
      if (prev) prev.addEventListener('click', () => { show(index - 1); start(); });
      if (next) next.addEventListener('click', () => { show(index + 1); start(); });
      bar.addEventListener('mouseenter', () => { hold = true; stop(); });
      bar.addEventListener('mouseleave', () => { hold = false; start(); });
      bar.addEventListener('focusin', () => { hold = true; stop(); });
      bar.addEventListener('focusout', () => { hold = false; start(); });
      d.addEventListener('visibilitychange', start);
      if (wide.addEventListener) wide.addEventListener('change', start);

      // Swipe on touch screens.
      let x0 = null;
      bar.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      bar.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        x0 = null;
        if (Math.abs(dx) < 30) return;
        const rtl = getComputedStyle(bar).direction === 'rtl';
        show(index + ((dx < 0) !== rtl ? 1 : -1));
        start();
      }, { passive: true });

      start();
    });
  };

  /* 4. Header ================================================================ */

  const initHeader = (root) => {
    root.querySelectorAll('[data-mn-header]').forEach((header) => {
      if (!once(header, 'header')) return;
      const section = header.closest('.shopify-section') || header;

      // Hairline under the bar once it sticks.
      if (hasIO) {
        const sentinel = d.createElement('div');
        sentinel.setAttribute('aria-hidden', 'true');
        sentinel.style.cssText = 'position:absolute;left:0;width:1px;height:1px;pointer-events:none;';
        sentinel.style.top = `${Math.max(0, section.offsetTop) + 1}px`;
        d.body.appendChild(sentinel);
        new IntersectionObserver(([entry]) => {
          header.classList.toggle('is-stuck', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        }).observe(sentinel);
      }

      // Search panel under the bar.
      const searchBtn = header.querySelector('[data-mn-search-toggle]');
      const panel = header.querySelector('[data-mn-search]');
      if (searchBtn && panel) {
        searchBtn.setAttribute('role', 'button');
        searchBtn.setAttribute('aria-expanded', 'false');
        const input = panel.querySelector('input[type="search"]');
        const setOpen = (open, focusBack) => {
          panel.hidden = !open;
          searchBtn.setAttribute('aria-expanded', String(open));
          searchBtn.setAttribute('aria-label', searchBtn.getAttribute(open ? 'data-mn-label-close' : 'data-mn-label-open') || '');
          if (open && input) input.focus();
          if (!open && focusBack) searchBtn.focus();
        };
        searchBtn.addEventListener('click', (e) => {
          e.preventDefault();
          setOpen(panel.hidden, false);
        });
        const close = panel.querySelector('[data-mn-search-close]');
        if (close) close.addEventListener('click', () => setOpen(false, true));
        panel.addEventListener('keydown', (e) => { if (e.key === 'Escape') setOpen(false, true); });
        d.addEventListener('click', (e) => {
          if (!panel.hidden && !panel.contains(e.target) && !searchBtn.contains(e.target)) setOpen(false, false);
        });
      }
    });

    // Side menu (round 9): drawer from the start edge; slides out before it closes (Escape, ×, overlay).
    root.querySelectorAll('[data-mn-menu-open]').forEach((btn) => {
      if (!once(btn, 'menu')) return;
      const dialog = d.getElementById(btn.getAttribute('data-mn-menu-open'));
      if (!dialog || typeof dialog.showModal !== 'function') return;
      let timer = 0;
      const finish = () => {
        clearTimeout(timer);
        if (!dialog.classList.contains('is-closing')) return;
        dialog.classList.remove('is-closing');
        if (dialog.open) dialog.close();
      };
      const shut = () => {
        if (!dialog.open || dialog.classList.contains('is-closing')) return;
        if (reduce.matches) {
          dialog.close();
          return;
        }
        dialog.classList.add('is-closing');
        timer = setTimeout(finish, 420);
      };
      dialog.addEventListener('animationend', (e) => { if (e.target === dialog) finish(); });
      btn.addEventListener('click', () => {
        dialog.classList.remove('is-closing');
        dialog.showModal();
        html.classList.add('mn-lock');
        btn.setAttribute('aria-expanded', 'true');
        const pressed = dialog.querySelector('[data-mn-menu-close]');
        if (pressed) pressed.focus();
      });
      dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        shut();
      });
      dialog.addEventListener('close', () => {
        clearTimeout(timer);
        dialog.classList.remove('is-closing');
        if (!d.querySelector('dialog[open]')) html.classList.remove('mn-lock');
        btn.setAttribute('aria-expanded', 'false');
        btn.focus({ preventScroll: true });
      });
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) {
          // The ::backdrop (outside the drawer) belongs to the dialog element.
          const r = dialog.getBoundingClientRect();
          if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) shut();
          return;
        }
        if (e.target.closest('[data-mn-menu-close]')) {
          shut();
          return;
        }
        const sub = e.target.closest('[data-mn-menu-sub]');
        if (sub) {
          const list = d.getElementById(sub.getAttribute('aria-controls'));
          const open = sub.getAttribute('aria-expanded') !== 'true';
          sub.setAttribute('aria-expanded', String(open));
          if (list) list.hidden = !open;
          return;
        }
        if (e.target.closest('[data-mn-menu-link]')) dialog.close();
      });
    });

    // Selects that submit on change (PDP shipping country until it opens the locale modal).
    root.querySelectorAll('select[data-mn-autosubmit]').forEach((sel) => {
      if (!once(sel, 'submit')) return;
      sel.addEventListener('change', () => {
        const form = sel.form;
        if (!form) return;
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.submit();
      });
    });

    // Cart badge: daile-cart.js rewrites <cart-count>; show the dot only above 0.
    root.querySelectorAll('[data-mn-cart-count]').forEach((badge) => {
      if (!once(badge, 'cart')) return;
      const counter = badge.querySelector('cart-count') || badge;
      const link = badge.closest('a');
      const label = link ? link.getAttribute('data-mn-cart-label') || '' : '';
      const sync = () => {
        const n = parseInt(counter.textContent, 10) || 0;
        badge.hidden = n < 1;
        if (link && label) link.setAttribute('aria-label', n > 0 ? `${label} (${n})` : label);
      };
      new MutationObserver(sync).observe(counter, { childList: true, characterData: true, subtree: true });
      sync();
    });
  };

  // <details data-mn-autoclose>: click outside or Escape closes.
  d.addEventListener('click', (e) => {
    d.querySelectorAll('details[data-mn-autoclose][open]').forEach((det) => {
      if (!det.contains(e.target)) det.removeAttribute('open');
    });
  });
  d.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    d.querySelectorAll('details[data-mn-autoclose][open]').forEach((det) => {
      det.removeAttribute('open');
      const summary = det.querySelector('summary');
      if (summary && det.contains(d.activeElement)) summary.focus();
    });
  });

  /* 5. Rails ================================================================= */

  const initRails = (root) => {
    root.querySelectorAll('[data-mn-rail]').forEach((wrap) => {
      if (!once(wrap, 'rail')) return;
      const track = wrap.querySelector('[data-mn-rail-track]');
      if (!track) return;
      const items = Array.from(track.children);
      const prev = wrap.querySelector('[data-mn-rail-prev]');
      const next = wrap.querySelector('[data-mn-rail-next]');
      const dotsBox = wrap.querySelector('[data-mn-rail-dots]');
      const nav = wrap.querySelector('.mn-rail-ui__nav');
      const dots = [];
      if (dotsBox) {
        dotsBox.textContent = '';
        items.forEach(() => {
          const dot = d.createElement('span');
          dotsBox.appendChild(dot);
          dots.push(dot);
        });
      }

      const sign = () => (getComputedStyle(track).direction === 'rtl' ? -1 : 1);
      const stepSize = () => {
        const first = items[0];
        if (!first) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return first.getBoundingClientRect().width + gap;
      };
      const go = (n) => track.scrollBy({ left: n * stepSize() * sign(), behavior: reduce.matches ? 'auto' : 'smooth' });
      if (prev) prev.addEventListener('click', () => go(-1));
      if (next) next.addEventListener('click', () => go(1));

      if (!hasIO) return;
      const ratio = items.map(() => 0);
      const update = () => {
        const full = (k) => ratio[k] >= 0.97;
        let first = ratio.findIndex((v) => v >= 0.55);
        if (first < 0) first = 0;
        const all = ratio.every((v) => v >= 0.97);
        if (nav) nav.style.visibility = all ? 'hidden' : '';
        if (prev) prev.disabled = full(0);
        if (next) next.disabled = full(items.length - 1);
        dots.forEach((dot, k) => dot.classList.toggle('is-active', k === first));
      };
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const k = items.indexOf(entry.target);
          if (k > -1) ratio[k] = entry.isIntersecting ? entry.intersectionRatio : 0;
        });
        update();
      }, { root: track, threshold: [0, 0.55, 0.97, 1] });
      items.forEach((item) => io.observe(item));
    });
  };

  /* 6 + 7. Video ============================================================= */

  const loadVideo = (video) => {
    let changed = false;
    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.getAttribute('data-src');
      source.removeAttribute('data-src');
      changed = true;
    });
    if (changed) video.load();
  };

  const reels = new Set();
  const pauseOthers = (current) => reels.forEach((v) => { if (v !== current && !v.paused) v.pause(); });

  const initReels = (root) => {
    root.querySelectorAll('[data-mn-reel]').forEach((reel) => {
      if (!once(reel, 'reel')) return;
      const video = reel.querySelector('video');
      const btn = reel.querySelector('[data-mn-reel-toggle]');
      if (!video || !btn) return;
      reels.add(video);
      const playLabel = btn.getAttribute('aria-label') || '';
      const pauseLabel = btn.getAttribute('data-mn-label-pause') || playLabel;

      btn.addEventListener('click', () => {
        if (video.paused) {
          pauseOthers(video);
          loadVideo(video);
          video.muted = false;
          const p = video.play();
          if (p && p.catch) {
            p.catch(() => {
              video.muted = true;
              video.play().catch(() => {});
            });
          }
        } else {
          video.pause();
        }
      });
      video.addEventListener('play', () => {
        reel.classList.add('is-playing');
        btn.setAttribute('aria-pressed', 'true');
        btn.setAttribute('aria-label', pauseLabel);
      });
      video.addEventListener('pause', () => {
        reel.classList.remove('is-playing');
        btn.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-label', playLabel);
      });
      if (hasIO) {
        new IntersectionObserver(([entry]) => {
          if (!entry.isIntersecting && !video.paused) video.pause();
        }, { threshold: 0.2 }).observe(reel);
      }
    });
  };

  const initAutoplay = (root) => {
    root.querySelectorAll('[data-mn-autoplay]').forEach((box) => {
      if (!once(box, 'auto')) return;
      const video = box.querySelector('video');
      if (!video) return;
      const btn = box.querySelector('[data-mn-autoplay-toggle]');
      const pauseLabel = btn ? btn.getAttribute('aria-label') || '' : '';
      const playLabel = btn ? btn.getAttribute('data-mn-label-play') || pauseLabel : '';
      let userPaused = reduce.matches;
      let onScreen = false;

      const setState = () => {
        const paused = video.paused;
        box.classList.toggle('is-paused', paused);
        if (btn) {
          btn.setAttribute('aria-pressed', String(paused));
          btn.setAttribute('aria-label', paused ? playLabel : pauseLabel);
        }
      };
      const play = () => {
        loadVideo(video);
        video.muted = true;
        const p = video.play();
        if (p && p.catch) p.catch(() => { box.classList.add('is-paused'); });
      };
      video.addEventListener('play', setState);
      video.addEventListener('pause', setState);
      setState();

      if (btn) {
        btn.addEventListener('click', () => {
          if (video.paused) {
            userPaused = false;
            play();
          } else {
            userPaused = true;
            video.pause();
          }
        });
      }
      if (hasIO) {
        new IntersectionObserver(([entry]) => {
          onScreen = entry.isIntersecting;
          if (onScreen && !userPaused && !design) play();
          else if (!onScreen && !video.paused) video.pause();
        }, { threshold: 0.25 }).observe(box);
      }
    });
  };

  /* Timeline wave ========================================================== */

  // Redraw the wave in pixels through the real icon centres (offsets ignore reveal transforms).
  // Desktop: smooth wave with horizontal tangents. Phone: quarter arcs — leave an icon sideways,
  // drop into the next one from above, so the line never crosses the step texts.
  const initTimeline = (root) => {
    root.querySelectorAll('[data-mn-timeline]').forEach((tl) => {
      if (!once(tl, 'tl')) return;
      const list = tl.querySelector('.mn-tl__list');
      const icons = Array.from(tl.querySelectorAll('.mn-tl__icon'));
      if (!list || icons.length < 1) return;
      const r = (n) => Math.round(n * 10) / 10;
      const centre = (icon) => {
        const step = icon.offsetParent === list ? icon : icon.closest('.mn-tl__step');
        const inStep = step === icon ? 0 : 1;
        const x = list.offsetLeft + step.offsetLeft + (inStep ? icon.offsetLeft : 0) + icon.offsetWidth / 2;
        const y = list.offsetTop + step.offsetTop + (inStep ? icon.offsetTop : 0) + icon.offsetHeight / 2;
        return [x, y];
      };
      const draw = () => {
        tl.querySelectorAll('.mn-tl__svg').forEach((svg) => {
          if (getComputedStyle(svg).display === 'none') return;
          const horizontal = svg.classList.contains('mn-tl__svg--h');
          const W = tl.clientWidth;
          const H = horizontal ? 96 : tl.clientHeight;
          if (!W || !H) return;
          const pts = icons.map(centre);
          let path = '';
          if (horizontal) {
            const [x0, y0] = pts[0];
            const [xl, yl] = pts[pts.length - 1];
            const ltr = pts.length < 2 || xl >= x0;
            path = `M${ltr ? 0 : r(W)} ${r(y0)} L${r(x0)} ${r(y0)}`;
            for (let i = 1; i < pts.length; i += 1) {
              const [ax, ay] = pts[i - 1];
              const [bx, by] = pts[i];
              const h = (bx - ax) / 2;
              path += ` C${r(ax + h)} ${r(ay)} ${r(bx - h)} ${r(by)} ${r(bx)} ${r(by)}`;
            }
            path += ` L${ltr ? r(W) : 0} ${r(yl)}`;
          } else {
            const k = 0.56;
            const [x0, y0] = pts[0];
            path = `M${r(x0)} 0 L${r(x0)} ${r(y0)}`;
            const arc = (ax, ay, bx, by) => ` C${r(ax + k * (bx - ax))} ${r(ay)} ${r(bx)} ${r(by - k * (by - ay))} ${r(bx)} ${r(by)}`;
            for (let i = 1; i < pts.length; i += 1) path += arc(pts[i - 1][0], pts[i - 1][1], pts[i][0], pts[i][1]);
            if (pts.length > 1) {
              const [xl, yl] = pts[pts.length - 1];
              path += arc(xl, yl, pts[pts.length - 2][0], H);
            }
          }
          svg.setAttribute('viewBox', `0 0 ${r(W)} ${r(H)}`);
          svg.removeAttribute('preserveAspectRatio');
          const p = svg.querySelector('path');
          if (p) p.setAttribute('d', path);
          svg.classList.add('is-px');
        });
      };
      draw();
      if ('ResizeObserver' in window) new ResizeObserver(draw).observe(tl);
      if (d.fonts && d.fonts.ready) d.fonts.ready.then(draw);
    });
  };

  /* 9. Preview links (catalog group, 2026-10-06) ============================
     While Mono is previewed through alternate templates (?view=mono…), keep the visitor in Mono:
     - internal links get the matching ?view=… (products → mono, custom-puzzle → mono-custom,
       collections → mono, daile-x-artist → mono-artists, /collections, /search, home → mono,
       pages → their mono view, blogs / articles → mono);
     - GET forms to such pages (search) get a hidden "view" field (a GET form drops the action's query);
     - return_to fields (localization form, cart forms) keep the view as well.
     Links that already carry ?view=…, other hosts, mailto:/tel:/#hash and [data-mn-view-skip] are left alone;
     the hash and the other parameters are kept. Markup added later (cart drawer, search suggestions,
     recommendations) is handled by a MutationObserver; a capturing click / submit handler is the safety net.

     Active ONLY on the mono layout (<html class="mn-html">) when the page itself was opened with ?view=mono…
     (or <html data-mn-preview>), never in the theme editor. Once the Mono templates are assigned for real
     (no ?view= in the address) it does nothing.

     Map: built-in defaults (PREVIEW_VIEWS) merged with every <script type="application/json" id="mn-view-map">
     / [data-mn-view-map] on the page (snippets/mn-view-map.liquid — other groups extend it there).
     Keys: paths without the locale prefix, exact ('/pages/faq') or a prefix ending in '*' ('/products/*');
     the longest prefix wins; value '' = do not touch. /collections/<c>/products/<p> counts as /products/<p>. */

  const PREVIEW_VIEWS = {
    '/': 'mono',
    '/collections': 'mono',
    '/collections/*': 'mono',
    '/collections/daile-x-artist': 'mono-artists',
    '/products/*': 'mono',
    '/products/custom-puzzle': 'mono-custom',
    '/search': 'mono',
    '/blogs/*': 'mono',
    '/pages/our-story': 'mono-our-story',
    '/pages/faq': 'mono-faq',
    '/pages/contact': 'mono-contact',
    '/pages/track-your-order': 'mono-track-order',
    '/pages/track-order': 'mono-track-order',
    '/pages/delivery-and-returns': 'mono-shipping',
    '/pages/shipping': 'mono-shipping',
    '/pages/highlights': 'mono-highlights',
    '/pages/for-creators': 'mono-creators',
    '/pages/daile-creators': 'mono-creators',
    '/pages/creators': 'mono-creators',
    '/pages/reviews': 'mono-reviews',
    '/pages/customizer': 'mono-customizer',
    '/pages/shipping-policy': 'mono-legal',
    '/pages/refund-policy': 'mono-legal',
    '/pages/terms-of-service': 'mono-legal',
    '/pages/privacy-policy': 'mono-legal',
    '/pages/data-sharing-opt-out': 'mono-legal',
    '/pages/affiliate-terms': 'mono-legal',
    
    '/pages/gift-guide': 'mono-gift-guide',
    '/pages/corporate-gifts': 'mono-corporate',
    '/pages/care-guide': 'mono-care',
    '/pages/gift-cards': 'mono-gift-cards',
    '/pages/payment-methods': 'mono-payment',
  };

  const initPreviewLinks = () => {
    if (design || !html.classList.contains('mn-html')) return;
    let current = '';
    try {
      current = new URL(window.location.href).searchParams.get('view') || '';
    } catch (e) {
      return;
    }
    if (!/^mono/i.test(current) && !html.hasAttribute('data-mn-preview')) return;

    const map = Object.assign({}, PREVIEW_VIEWS);
    d.querySelectorAll('script#mn-view-map, script[data-mn-view-map]').forEach((node) => {
      try {
        const extra = JSON.parse(node.textContent || '{}');
        if (extra && typeof extra === 'object') Object.assign(map, extra);
      } catch (e) { /* malformed map: keep the defaults */ }
    });

    const tidy = (path) => {
      const p = String(path || '/').toLowerCase().replace(/\/+$/, '');
      return p || '/';
    };
    const exact = new Map();
    const prefixes = [];
    Object.keys(map).forEach((key) => {
      const value = typeof map[key] === 'string' ? map[key].trim() : '';
      if (key.slice(-1) === '*') prefixes.push([key.slice(0, -1).toLowerCase(), value]);
      else exact.set(tidy(key), value);
    });
    prefixes.sort((a, b) => b[0].length - a[0].length);

    const root = String((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').toLowerCase();
    const SECTIONS = /^\/(?:products|collections|pages|blogs|search|cart|account|policies)(?:\/|$)/;
    const LOCALE = /^\/[a-z]{2}(?:-[a-z]{2,4})?(?=\/|$)/;

    // '/de/collections/x' → '/collections/x'; '/collections/c/products/p' → '/products/p'.
    const basePath = (pathname) => {
      let p;
      try {
        p = decodeURIComponent(pathname);
      } catch (e) {
        p = pathname;
      }
      p = p.toLowerCase();
      if (root !== '/' && (p === root.slice(0, -1) || p.indexOf(root) === 0)) {
        p = p.slice(root.length - 1) || '/';
      } else {
        const m = LOCALE.exec(p);
        if (m) {
          const rest = p.slice(m[0].length) || '/';
          if (rest === '/' || SECTIONS.test(rest)) p = rest;
        }
      }
      const nested = /^\/collections\/[^/]+(\/products\/[^/]+)/.exec(p);
      if (nested) p = nested[1];
      return tidy(p);
    };

    const viewFor = (path) => {
      if (exact.has(path)) return exact.get(path);
      for (let i = 0; i < prefixes.length; i += 1) {
        if (path.indexOf(prefixes[i][0]) === 0) return prefixes[i][1];
      }
      return '';
    };

    const parse = (raw) => {
      const t = String(raw || '').trim();
      if (!t || t.charAt(0) === '#' || /^(?:mailto|tel|sms|javascript|data|blob):/i.test(t)) return null;
      try {
        const url = new URL(t, window.location.href);
        return url.origin === window.location.origin ? url : null;
      } catch (e) {
        return null;
      }
    };

    // URL with ?view=… added, or null when it should stay as it is.
    const withView = (raw) => {
      const url = parse(raw);
      if (!url || url.searchParams.has('view')) return null;
      const view = viewFor(basePath(url.pathname));
      if (!view) return null;
      url.searchParams.set('view', view);
      return url;
    };
    const relative = (url) => url.pathname + url.search + url.hash;

    const fixLink = (a) => {
      if (a.hasAttribute('data-mn-view-skip') || a.hasAttribute('download')) return;
      const url = withView(a.getAttribute('href'));
      if (url) a.setAttribute('href', relative(url));
    };

    const fixForm = (form) => {
      if (form.hasAttribute('data-mn-view-skip')) return;
      const method = (form.getAttribute('method') || 'get').toLowerCase();
      if (method === 'get') {
        if (form.querySelector('[name="view"]')) return;
        const url = parse(form.getAttribute('action') || window.location.href);
        if (!url) return;
        const view = url.searchParams.get('view') || viewFor(basePath(url.pathname));
        if (!view) return;
        const field = d.createElement('input');
        field.type = 'hidden';
        field.name = 'view';
        field.value = view;
        form.appendChild(field);
        return;
      }
      form.querySelectorAll('input[name="return_to"]').forEach((field) => {
        const url = withView(field.value);
        if (url) field.value = relative(url);
      });
    };

    const scan = (node) => {
      if (!node || node.nodeType !== 1) return;
      if (node.matches('a[href]')) fixLink(node);
      else if (node.matches('form')) fixForm(node);
      node.querySelectorAll('a[href]').forEach(fixLink);
      node.querySelectorAll('form').forEach(fixForm);
    };
    scan(d.documentElement);

    if ('MutationObserver' in window) {
      new MutationObserver((records) => {
        records.forEach((record) => record.addedNodes.forEach(scan));
      }).observe(d.documentElement, { childList: true, subtree: true });
    }
    const onActivate = (e) => {
      const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (a) fixLink(a);
    };
    d.addEventListener('click', onActivate, true);
    d.addEventListener('auxclick', onActivate, true);
    d.addEventListener('submit', (e) => {
      if (e.target && e.target.tagName === 'FORM') fixForm(e.target);
    }, true);
  };

  /* 10. Promo hero sign-up (sections/mn-promo, round 7) ====================
     With window.mnOffer (assets/mn-offer.js, offer popup group): mnOffer.subscribe(email, { source: 'hero' })
     → Promise; resolved = done (unless { ok: false }), rejected = error (a user-facing Error's message is shown).
     Without it the form is posted natively (Shopify customer form) and the section renders the result after the
     reload. Inline states in an aria-live region; the e-mail row hides on success — also when the visitor signs
     up in the offer pop-up ('mn-offer:subscribed'). */

  const initPromo = (root) => {
    root.querySelectorAll('[data-mn-promo-form]').forEach((form) => {
      if (!once(form, 'promo')) return;
      const input = form.querySelector('input[type="email"]');
      const field = form.querySelector('[data-mn-promo-field]');
      const msg = form.querySelector('[data-mn-promo-msg]');
      const note = form.querySelector('[data-mn-promo-note]');
      const btn = form.querySelector('button[type="submit"]');
      let busy = false;

      const show = (kind, text) => {
        if (!msg) return;
        msg.classList.toggle('is-error', kind === 'error');
        msg.classList.toggle('is-done', kind === 'done');
        msg.textContent = kind ? text || msg.getAttribute(kind === 'done' ? 'data-mn-success' : 'data-mn-error') || '' : '';
      };
      // The e-mail row is inline on every screen (round 10b phone: one compact row under the gold offer).
      const finish = () => {
        if (input) input.removeAttribute('aria-invalid');
        if (field) field.hidden = true;
        if (note) note.hidden = true;
        show('done');
      };
      d.addEventListener('mn-offer:subscribed', () => { if (!busy && field && !field.hidden) finish(); });
      const setBusy = (on) => {
        busy = on;
        form.classList.toggle('is-busy', on);
        if (on) form.setAttribute('aria-busy', 'true');
        else form.removeAttribute('aria-busy');
        if (btn) btn.disabled = on;
      };

      if (input) {
        input.addEventListener('input', () => {
          if (msg && msg.classList.contains('is-error')) {
            show('');
            input.removeAttribute('aria-invalid');
          }
        });
      }

      form.addEventListener('submit', (e) => {
        const offer = window.mnOffer;
        if (!offer || typeof offer.subscribe !== 'function' || !input) return; // native Shopify form
        // Handled here: keep Shopify's own customer-form handler (bot challenge + post) out of it.
        e.preventDefault();
        e.stopPropagation();
        if (busy) return;
        const email = input.value.trim();
        if (!email || (input.checkValidity && !input.checkValidity())) {
          if (input.reportValidity) input.reportValidity();
          return;
        }
        setBusy(true);
        let job;
        try {
          job = Promise.resolve(offer.subscribe(email, { source: 'hero' }));
        } catch (err) {
          job = Promise.reject(err);
        }
        job.then((res) => {
          if (res && (res.ok === false || res.success === false)) throw res;
          setBusy(false);
          finish();
          if (msg) msg.focus({ preventScroll: true });
        }).catch((err) => {
          setBusy(false);
          input.setAttribute('aria-invalid', 'true');
          show('error', err && err.userFacing && err.message ? String(err.message) : '');
          input.focus();
        });
      });
    });
  };

  /* 11. Gift-set chooser (sections/mn-sets, round 7) ========================
     "Choose" buttons [data-mn-chooser="<dialog id>"] carry the set (name / spec / price) and both links
     (data-mn-personal, data-mn-art). One native modal <dialog> per section: Escape closes, the page behind is
     inert (focus stays inside), a click on the overlay or × closes, focus returns to the button.
     Phone: bottom sheet; desktop: centred (assets/mn.css 6f-2). */

  const initChooser = (root) => {
    root.querySelectorAll('[data-mn-chooser]').forEach((btn) => {
      if (!once(btn, 'chooser')) return;
      btn.addEventListener('click', () => {
        const dialog = d.getElementById(btn.getAttribute('data-mn-chooser'));
        const personal = btn.getAttribute('data-mn-personal') || '';
        if (!dialog || typeof dialog.showModal !== 'function') {
          if (personal) window.location.href = personal;
          return;
        }
        const fill = (sel, text) => {
          const el = dialog.querySelector(sel);
          if (!el) return;
          el.textContent = text || '';
          el.hidden = !text;
        };
        fill('[data-mn-chooser-name]', btn.getAttribute('data-mn-set-name'));
        fill('[data-mn-chooser-spec]', btn.getAttribute('data-mn-set-spec'));
        // DAILE Friday (assets/mn-dfriday.js) repaints the chooser price from these base cents.
        const cpEl = dialog.querySelector('[data-mn-chooser-price]');
        const cpCents = btn.getAttribute('data-mn-set-cents');
        if (cpEl && cpCents) cpEl.setAttribute('data-mn-price-cents', cpCents);
        else if (cpEl) cpEl.removeAttribute('data-mn-price-cents');
        fill('[data-mn-chooser-price]', btn.getAttribute('data-mn-set-price'));
        // The chosen set's box cut-out (round 10) in a fixed-ratio frame; no picture → frame hidden.
        const box = dialog.querySelector('[data-mn-chooser-box]');
        const boxImg = box && box.querySelector('[data-mn-chooser-img]');
        const boxSrc = btn.getAttribute('data-mn-set-img') || '';
        if (box && boxImg) {
          if (boxSrc && boxImg.getAttribute('src') !== boxSrc) boxImg.setAttribute('src', boxSrc);
          box.hidden = !boxSrc;
        }
        const link =(sel, href) => {
          const a = dialog.querySelector(sel);
          if (!a) return;
          if (href) {
            a.setAttribute('href', href);
            a.parentElement.hidden = false;
          } else {
            a.parentElement.hidden = true;
          }
        };
        link('[data-mn-chooser-personal]', personal);
        link('[data-mn-chooser-art]', btn.getAttribute('data-mn-art'));

        dialog.mnOpener = btn;
        if (!once(dialog, 'chooser')) {
          // listeners already bound
        } else {
          dialog.addEventListener('click', (e) => {
            if (e.target === dialog || e.target.closest('[data-mn-chooser-close]')) dialog.close();
          });
          dialog.addEventListener('close', () => {
            html.classList.remove('mn-lock');
            const opener = dialog.mnOpener;
            dialog.mnOpener = null;
            if (opener && opener.isConnected) opener.focus({ preventScroll: true });
          });
        }
        dialog.showModal();
        html.classList.add('mn-lock');
        const first = dialog.querySelector('[data-mn-chooser-personal]');
        if (first && !first.parentElement.hidden) first.focus({ preventScroll: true });
      });
    });
  };

  /* 12. Country & language modal (snippets/mn-locale, round 9) =============
     The only locale selector on Mono pages. Opened by [data-mn-locale-open] (header bar, side menu, footer —
     snippets/mn-locale-btn) or window.mnLocale.open() (e.g. the PDP "Change" link). Country change → the
     Language options become that country's languages (data-langs; the current language stays when it is
     offered) and the read-only Currency shows its currency. Native modal <dialog>: Escape / × / overlay close,
     focus returns to the trigger. Submit = Shopify's localization form (POST /localization → reload). */

  const mnLocale = (() => {
    let opener = null;
    let names = null;
    let startLang = '';
    const dialog = () => d.querySelector('[data-mn-locale]');

    const sync = (dlg) => {
      const country = dlg.querySelector('[data-mn-lc-country]');
      const lang = dlg.querySelector('[data-mn-lc-lang]');
      const cur = dlg.querySelector('[data-mn-lc-cur]');
      const opt = country ? country.options[country.selectedIndex] : null;
      if (!opt) return;
      if (cur) {
        const code = opt.getAttribute('data-cur') || '';
        const sym = opt.getAttribute('data-sym') || '';
        cur.value = sym && sym !== code ? `${sym} ${code}` : code;
      }
      if (!lang) return;
      const codes = (opt.getAttribute('data-langs') || '').split(',').filter(Boolean);
      if (!codes.length) return;
      if (!names) {
        try {
          names = JSON.parse((dlg.querySelector('[data-mn-lc-names]') || {}).textContent || '{}');
        } catch (e) {
          names = {};
        }
      }
      const keep = lang.value || startLang;
      if (Array.from(lang.options).map((o) => o.value).join(',') !== codes.join(',')) {
        const labels = {};
        Array.from(lang.options).forEach((o) => { labels[o.value] = o.textContent; });
        lang.textContent = '';
        codes.forEach((code) => {
          const o = d.createElement('option');
          o.value = code;
          o.lang = code;
          o.textContent = names[code] || labels[code] || code;
          lang.appendChild(o);
        });
      }
      lang.value = codes.indexOf(keep) > -1 ? keep : codes.indexOf(startLang) > -1 ? startLang : codes[0];
    };

    const setup = (dlg) => {
      if (!once(dlg, 'locale')) return;
      const form = dlg.querySelector('form');
      const country = dlg.querySelector('[data-mn-lc-country]');
      const lang = dlg.querySelector('[data-mn-lc-lang]');
      startLang = lang ? lang.value : '';
      if (country) country.addEventListener('change', () => sync(dlg));
      dlg.addEventListener('click', (e) => {
        if (e.target.closest('[data-mn-locale-close]')) {
          dlg.close();
          return;
        }
        if (e.target !== dlg) return;
        const r = dlg.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dlg.close();
      });
      dlg.addEventListener('close', () => {
        if (!d.querySelector('dialog[open]')) html.classList.remove('mn-lock');
        const back = opener;
        opener = null;
        if (back && back.isConnected && back.getClientRects().length) back.focus({ preventScroll: true });
        d.dispatchEvent(new CustomEvent('mn-locale:close'));
      });
      if (form) {
        form.addEventListener('submit', (e) => {
          if (form.getAttribute('aria-busy') === 'true') {
            e.preventDefault();
            return;
          }
          form.setAttribute('aria-busy', 'true');
          // A manual language choice ends the automatic browser-language switch (snippets/mn-lang-auto).
          try { localStorage.setItem('mn-lang-choice', (lang && lang.value) || '1'); } catch (err) {}
          // bfcache: a "back" to this page must not keep the busy state.
          window.addEventListener('pageshow', () => form.removeAttribute('aria-busy'), { once: true });
        });
      }
    };

    const open = (from) => {
      const dlg = dialog();
      if (!dlg || typeof dlg.showModal !== 'function') return false;
      setup(dlg);
      if (dlg.open) return true;
      opener = from && from.focus ? from : d.activeElement && d.activeElement !== d.body ? d.activeElement : null;
      // Fresh state: the page's own country / language every time it opens.
      const form = dlg.querySelector('form');
      if (form) {
        form.reset();
        form.removeAttribute('aria-busy');
      }
      const lang = dlg.querySelector('[data-mn-lc-lang]');
      if (lang) lang.value = startLang;
      sync(dlg);
      dlg.showModal();
      html.classList.add('mn-lock');
      // Focus the title: screen readers announce the dialog, no ring on a field before the visitor picks one.
      const first = dlg.querySelector('#mn-locale-title') || dlg.querySelector('[data-mn-locale-close]');
      if (first) first.focus({ preventScroll: true });
      d.dispatchEvent(new CustomEvent('mn-locale:open'));
      return true;
    };

    const close = () => {
      const dlg = dialog();
      if (dlg && dlg.open) dlg.close();
    };

    d.addEventListener('click', (e) => {
      const trigger = e.target && e.target.closest ? e.target.closest('[data-mn-locale-open]') : null;
      if (!trigger) return;
      if (open(trigger)) e.preventDefault();
    });

    return Object.freeze({ open, close });
  })();
  window.mnLocale = mnLocale;

  /* 13. Card photo on touch (round 12, 2026-10-08) ==========================
     Owner: phones have no hover, so where a card's first photo is not the framed artwork (box / Mini set), a light
     touch (even brushing past while scrolling) shows the framed one; a real tap opens the artwork right away.
     Cards: [data-mn-tswap] (snippets/mn-cat-card, sections/mn-collection): 'alt' = the hover photo is the framed one,
     'touch' = an extra touch-only framed photo. touchstart → .is-tswap (CSS: the same crossfade as the desktop hover);
     kept while the finger is down, also while it scrolls the page; back 1.2 s after the finger lifts, or at once when
     another card is touched. Passive listeners, never preventDefault → taps and scrolling work as before.
     Phones download the framed photo only once the card is near the screen after the page has loaded
     ([data-mn-tswap-armed] lifts its display:none, assets/mn-cat.css / mn.css) — never before the first images. */

  const initTouchSwap = () => {
    if (!('ontouchstart' in window) && !(navigator.maxTouchPoints > 0)) return null;
    const SEL = '[data-mn-tswap]';
    const HOLD = 1200;
    let card = null;
    let down = false;
    let timer = 0;
    const arm = (el) => {
      if (!el.hasAttribute('data-mn-tswap-armed')) el.setAttribute('data-mn-tswap-armed', '');
    };
    const reset = () => {
      clearTimeout(timer);
      if (card) card.classList.remove('is-tswap');
      card = null;
      down = false;
    };
    d.addEventListener('touchstart', (e) => {
      const t = e.target;
      const el = t && t.closest ? t.closest(SEL) : null;
      if (!el) return;
      if (card !== el) reset();
      clearTimeout(timer);
      arm(el);
      el.classList.add('is-tswap');
      card = el;
      down = true;
    }, { passive: true });
    const lift = (e) => {
      if (!card || !down || (e.touches && e.touches.length)) return;
      down = false;
      clearTimeout(timer);
      timer = setTimeout(reset, HOLD);
    };
    d.addEventListener('touchend', lift, { passive: true });
    d.addEventListener('touchcancel', lift, { passive: true });
    // Back from the artwork page (bfcache): the card shows its own photo again.
    window.addEventListener('pageshow', (e) => { if (e.persisted) reset(); });

    let io = null;
    const prime = (root) => {
      const list = (root || d).querySelectorAll(`${SEL}:not([data-mn-tswap-armed])`);
      if (!list.length) return;
      if (!hasIO) {
        list.forEach(arm);
        return;
      }
      if (!io) {
        io = new IntersectionObserver((entries) => entries.forEach((en) => {
          if (!en.isIntersecting) return;
          arm(en.target);
          io.unobserve(en.target);
        }), { rootMargin: '50% 0px' });
      }
      list.forEach((el) => io.observe(el));
    };
    const start = () => {
      if (window.requestIdleCallback) window.requestIdleCallback(() => prime(d), { timeout: 2000 });
      else setTimeout(() => prime(d), 200);
    };
    if (d.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
    return prime;
  };
  const primeTouchSwap = initTouchSwap();

  /* Init ===================================================================== */

  const init = (root) => {
    initAnn(root);
    initHeader(root);
    initRails(root);
    initReels(root);
    initAutoplay(root);
    initTimeline(root);
    initPromo(root);
    initChooser(root);
    initReveal(root);
  };

  init(d);
  initPreviewLinks();

  d.addEventListener('shopify:section:load', (e) => {
    init(e.target);
    if (primeTouchSwap) primeTouchSwap(e.target);
  });
  d.addEventListener('shopify:block:select', (e) => {
    const block = e.target;
    if (block && block.closest && block.closest('[data-mn-rail-track]')) {
      block.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  });
})();
